/**
 * KONTRAKT 1.0 - klient dwoch endpointow serwerowej sciezki fiskalnej.
 *
 *   ZAPIS 1  POST /api/transactions/<id>/fiscal-receipt
 *            serwer buduje paragon, zaklada slad proby druku i oddaje gotowy
 *            kontrakt (same ciagi znakow do wklejenia w pakiety XML).
 *
 *   ZAPIS 2  POST /api/transactions/fiscal-printout/<wydruk_id>/report
 *            raport z kabla: co wyslano, co drukarka odpowiedziala, jaki jest
 *            wynik (wydrukowany / niewydrukowany / nie_wiadomo). TEN SAM endpoint
 *            ustawia pos_transakcje.fiskalizacja, wiec status i slad powstaja atomowo.
 *
 * Ten modul NICZEGO nie liczy - przenosi bajty tam i z powrotem.
 */
import api from './api';
import { AKCEPTOWANE_WERSJE } from './fiscal/receiptContract';

// Przelacznik wdrozeniowy. Domyslnie WYLACZONY: dopoki wlasciciel nie zrobi
// wydruku probnego na fizycznej drukarce, POS jedzie stara sciezka.
// Wlaczenie: localStorage.setItem('fiskalny_kontrakt', '1')
const KLUCZ_PRZELACZNIKA = 'fiskalny_kontrakt';

// Kolejka raportow, ktorych nie udalo sie odeslac (padl internet, restart backendu).
// Bez niej kazde zerwanie sieci po druku kasuje jedyny slad tego, co poszlo na papier.
const KLUCZ_KOLEJKI = 'fiskalne_raporty_kolejka';
const MAX_W_KOLEJCE = 20;

export function kontraktWlaczony() {
  try {
    return localStorage.getItem(KLUCZ_PRZELACZNIKA) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * Mapuje trzy stany weryfikacji wydruku na slownik wyniku serwera.
 * Zasada 5: 'unknown' NIE jest ani sukcesem, ani porazka - ma wlasna nazwe.
 */
export function wynikZWeryfikacji(printed) {
  if (printed === 'yes') return 'wydrukowany';
  if (printed === 'no') return 'niewydrukowany';
  return 'nie_wiadomo';
}

function odczytajBlad(error) {
  const dane = error?.response?.data;
  return {
    kod: dane?.error_code || error?.errorCode || 'BLAD_SIECI',
    komunikat: dane?.error || dane?.message || error?.message || 'Nie udalo sie pobrac paragonu z serwera',
    status: error?.response?.status || null,
  };
}

/**
 * ZAPIS 1 - pobierz gotowy paragon z serwera.
 *
 * @param {number} transakcjaId
 * @param {object} [opcje] - { powod: 'sprzedaz'|'ponowny', stanowisko }
 * @returns {Promise<{ok: boolean, kontrakt?: object, kod?: string, komunikat?: string}>}
 */
export async function pobierzKontrakt(transakcjaId, opcje = {}) {
  if (!transakcjaId) {
    return { ok: false, kod: 'BRAK_TRANSAKCJI', komunikat: 'Brak numeru transakcji - nie ma czego drukowac' };
  }
  try {
    const odpowiedz = await api.post(
      `/transactions/${transakcjaId}/fiscal-receipt`,
      {
        akceptowane_wersje: AKCEPTOWANE_WERSJE,
        powod: opcje.powod || 'sprzedaz',
        protokol: 'xml',
        ...(opcje.stanowisko ? { stanowisko: opcje.stanowisko } : {}),
      },
      // silent: wlasny, precyzyjny komunikat zamiast ogolnego dymka z interceptora.
      { silent: true }
    );

    const dane = odpowiedz?.data;
    if (!dane || dane.success === false || !dane.data) {
      return {
        ok: false,
        kod: dane?.error_code || 'BLAD_BUDOWY',
        komunikat: dane?.error || dane?.message || 'Serwer nie zwrocil paragonu',
      };
    }
    return { ok: true, kontrakt: dane.data };
  } catch (error) {
    console.error('[FISCAL-KONTRAKT] nie udalo sie pobrac paragonu:', error);
    return { ok: false, ...odczytajBlad(error) };
  }
}

/**
 * ZAPIS 2 - odeslij raport z kabla.
 * Przy bledzie sieci raport laduje w kolejce localStorage i idzie przy nastepnej okazji.
 *
 * @returns {Promise<{ok: boolean, zakolejkowany?: boolean, dane?: object, komunikat?: string}>}
 */
export async function wyslijRaport(wydrukId, raport) {
  if (!wydrukId) {
    return { ok: false, komunikat: 'Brak wydruk_id - proba druku nie ma sladu w bazie' };
  }
  try {
    const odpowiedz = await api.post(
      `/transactions/fiscal-printout/${wydrukId}/report`,
      raport,
      { silent: true }
    );
    return { ok: true, dane: odpowiedz?.data?.data };
  } catch (error) {
    const status = error?.response?.status;
    // 4xx (poza 429) to blad tresci raportu - ponawianie go nie naprawi.
    if (status && status >= 400 && status < 500 && status !== 429) {
      console.error('[FISCAL-KONTRAKT] serwer odrzucil raport z druku:', status, error?.response?.data);
      return { ok: false, komunikat: odczytajBlad(error).komunikat };
    }
    console.warn('[FISCAL-KONTRAKT] raport z druku do kolejki (blad sieci):', error?.message);
    zakolejkuj(wydrukId, raport);
    return { ok: false, zakolejkowany: true, komunikat: odczytajBlad(error).komunikat };
  }
}

// ---------------------------------------------------------------------------
// Kolejka raportow
// ---------------------------------------------------------------------------

function wczytajKolejke() {
  try {
    const surowe = JSON.parse(localStorage.getItem(KLUCZ_KOLEJKI) || '[]');
    return Array.isArray(surowe) ? surowe : [];
  } catch (_) {
    return [];
  }
}

function zapiszKolejke(kolejka) {
  try {
    localStorage.setItem(KLUCZ_KOLEJKI, JSON.stringify(kolejka.slice(-MAX_W_KOLEJCE)));
  } catch (_) {
    // Brak miejsca w localStorage nie moze wywrocic druku.
  }
}

function zakolejkuj(wydrukId, raport) {
  const kolejka = wczytajKolejke().filter(w => w.wydruk_id !== wydrukId);
  kolejka.push({ wydruk_id: wydrukId, raport, dodano: Date.now() });
  zapiszKolejke(kolejka);
}

/**
 * Dosyla zalegle raporty. Wolane przed kolejnym drukiem i przy starcie hooka.
 * Endpoint jest idempotentny per wydruk_id, wiec powtorka nie zaszkodzi:
 * slabszy wynik nigdy nie nadpisze mocniejszego.
 */
export async function dosliZalegleRaporty() {
  const kolejka = wczytajKolejke();
  if (kolejka.length === 0) return { wyslane: 0, zostalo: 0 };

  const zostale = [];
  let wyslane = 0;
  for (const wpis of kolejka) {
    try {
      await api.post(`/transactions/fiscal-printout/${wpis.wydruk_id}/report`, wpis.raport, { silent: true });
      wyslane += 1;
    } catch (error) {
      const status = error?.response?.status;
      if (status && status >= 400 && status < 500 && status !== 429) {
        // Serwer odrzucil trwale - trzymanie tego w kolejce w nieskonczonosc
        // tylko zasmieca i opoznia kolejne raporty.
        console.error('[FISCAL-KONTRAKT] zalegly raport odrzucony na stale:', wpis.wydruk_id, status);
        continue;
      }
      zostale.push(wpis);
    }
  }
  zapiszKolejke(zostale);
  return { wyslane, zostalo: zostale.length };
}

export function liczbaZaleglychRaportow() {
  return wczytajKolejke().length;
}

const fiscalReceiptService = {
  kontraktWlaczony,
  pobierzKontrakt,
  wyslijRaport,
  dosliZalegleRaporty,
  liczbaZaleglychRaportow,
  wynikZWeryfikacji,
};

export default fiscalReceiptService;
